// =============================================================================
// IMAGE SHOWCASE COMPONENT (V2 - NORMALIZED)
// =============================================================================

import React from 'react';
import { Button } from '@/components/Button';
import { createActionHandler, convertButtonToAction } from '@/utils/actionHandler';
import { layoutToClasses, displayToStyles } from '@/types/layout';
import type { ContentComponent } from '@/types';
import type { ImageShowcaseComponent } from '@/types/components';

interface ImageShowcaseProps {
  component: ContentComponent;
}

/**
 * Type guard for new ImageShowcaseComponent format
 */
function isNewImageShowcaseFormat(component: ContentComponent): boolean {
  return (
    component.type === 'image-showcase' &&
    'content' in component &&
    typeof (component as any).content === 'object'
  );
}

/**
 * Image Showcase Component
 */
export const ImageShowcase: React.FC<ImageShowcaseProps> = ({ component }) => {
  if (isNewImageShowcaseFormat(component)) {
    return <ImageShowcaseNew component={component as unknown as any} />;
  }
  return <ImageShowcaseLegacy component={component} />;
};

/**
 * New format Image Showcase
 */
const ImageShowcaseNew: React.FC<{ component: ImageShowcaseComponent }> = ({ component }) => {
  const { id, content, media, actions, layout, display } = component;
  
  const layoutClasses = layoutToClasses(layout);
  const displayStyles = displayToStyles(display);
  
  const showOverlay = display.background?.overlay !== undefined;
  const textColor = 'white'; // Default text color for showcases
  
  return (
    <section
      id={id}
      className={`image-showcase ${media?.image ? 'has-background-image' : ''} ${layoutClasses}`}
      style={displayStyles}
    >
      {/* Background Overlay */}
      {showOverlay && display.background?.overlay && (
        <div
          className="image-showcase-overlay"
          style={{
            opacity: display.background.overlay.opacity || 0.6,
            backgroundColor: display.background.overlay.color || 'rgba(0, 0, 0, 0.6)',
          }}
        />
      )}
      
      {/* Content Container */}
      <div className="image-showcase-content">
        <div className="container">
          <div
            className="image-showcase-text"
            style={{ color: textColor }}
          >
            {content.title && <h2 className="image-showcase-title">{content.title}</h2>}
            {content.body && <p className="image-showcase-description">{content.body}</p>}
            
            {actions && actions.length > 0 && (
              <div className="image-showcase-actions">
                {actions.map((action, index) => (
                  <Button
                    key={action.id || index}
                    variant={action.variant || 'primary'}
                    size={action.size || 'lg'}
                    onClick={createActionHandler(action)}
                    disabled={action.disabled}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

/**
 * Legacy format Image Showcase
 */
const ImageShowcaseLegacy: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const buttons = metadata?.buttons || [];
  const backgroundImage = metadata?.backgroundImage || metadata?.image;
  const overlayOpacity = metadata?.overlayOpacity || 0.6;
  const textAlign = metadata?.textAlign || 'center';
  const minHeight = metadata?.minHeight || '70vh';
  const overlayColor = metadata?.overlayColor || 'rgba(0, 0, 0, 0.6)';
  const textColor = metadata?.textColor || 'white';
  const showOverlay = metadata?.showOverlay !== false;
  
  const actions = buttons.map(convertButtonToAction);
  
  return (
    <section
      id={id}
      className={`image-showcase ${backgroundImage ? 'has-background-image' : ''}`}
      style={{
        backgroundImage: backgroundImage ? `url(${backgroundImage})` : undefined,
        minHeight: minHeight,
        textAlign: textAlign as React.CSSProperties['textAlign']
      }}
    >
      {/* Background Overlay */}
      {showOverlay && (
        <div
          className="image-showcase-overlay"
          style={{
            opacity: overlayOpacity,
            backgroundColor: overlayColor
          }}
        />
      )}
      
      {/* Content Container */}
      <div className="image-showcase-content">
        <div className="container">
          <div
            className="image-showcase-text"
            style={{ color: textColor }}
          >
            {title && <h2 className="image-showcase-title">{title}</h2>}
            {content && <p className="image-showcase-description">{content}</p>}
            
            {actions.length > 0 && (
              <div className="image-showcase-actions">
                {actions.map((action, index) => (
                  <Button
                    key={index}
                    variant={action.variant || 'primary'}
                    size={action.size || 'lg'}
                    onClick={createActionHandler(action)}
                  >
                    {action.label}
                  </Button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export { ImageShowcaseNew, ImageShowcaseLegacy };

